import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  api_portalOfficeListing_filter,
  service_ResponseForNonMemberPortalOfficeListing,
} from '@/init/swagger';
import { CardWrapper, useLogic } from './logic';
import { MuiButton } from '@/components/button/MuiButton';
import { MuiChip } from '@/components/chip/MuiChip';
import NoImagePNG from '@/../public/no-image.png';
import { OnlyMemberComponent } from '@/features/public/OnlyMemberComponent';
import { DxStationLabel } from '@/features/label/DxStationLabel';
import { CanRequestLabel } from '@/features/label/CanRequestLabel';
import { PhoneIcon } from '@/features/icon/PhoneIcon';
import { HpUrlIcon } from '@/features/icon/HpUrlIcon';
import { ServiceList } from '../ServiceList';
import { PartnershipsSummary } from '@/features/partnership/components/PartnershipsSummary';

type Props = {
  listing: service_ResponseForNonMemberPortalOfficeListing;
  isHighlighted?: boolean;
  isShowMap?: boolean;
  searchFilters: api_portalOfficeListing_filter[];
};

export function PublicListingListItem({
  listing,
  isHighlighted,
  isShowMap,
  searchFilters,
}: Props) {
  const {
    postCode,
    address,
    publishedOnStr,
    updatedOnStr,
    onClickRequest,
    href,
    isDxStation,
    isCarelogood,
    tel,
    hasServiceCategoryItems,
    serviceCategoryItems,
  } = useLogic({
    listing,
    searchFilters,
  });

  return (
    <CardWrapper href={href}>
      <Box
        sx={{
          containerName: 'hospital-listing-container',
          containerType: 'inline-size',
        }}
      >
        <Card
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            boxShadow:
              isShowMap && isHighlighted
                ? `0px 0px 4px 0px rgba(62, 201, 190, 0.3), 0px 8px 16px 0px rgba(62, 201, 190, 0.2)`
                : `0px 0px 2px 0px rgba(0, 0, 0, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.10)`,
            borderRadius: 2,
            padding: 2,
            gap: {
              xs: 2,
              md: 3,
            },
            backgroundColor: 'white',
            border: isShowMap && isHighlighted ? '2px solid #3ec9be' : 'none',
            '&:hover': {
              '& .highlight span': {
                textDecoration: 'underline',
                textDecorationColor: '#3ec9be',
                color: '#3ec9be',
              },
              '& .MuiCardMedia-img': {
                border: 'solid 2px #3ec9be',
              },
            },
            '@container hospital-listing-container (width < 600px)': {
              flexDirection: 'column',
              gap: 2,
            },
          }}
        >
          <CardMedia
            component="img"
            sx={{
              width: {
                xs: '100%',
                md: 320,
              },
              maxWidth: {
                xs: '100%',
                md: '40%',
              },
              aspectRatio: '16 / 9',
              height: {
                xs: 'auto',
                md: 220,
              },
              borderRadius: 1,
              margin: 'auto',
              '@container hospital-listing-container (width < 600px)': {
                width: '100%',
                maxWidth: '100%',
                aspectRatio: '16 / 9',
                height: 'auto',
              },
            }}
            image={
              listing.portal_office_listing_image?.image1_url || NoImagePNG.src
            }
            alt={listing.portal_office_listing_image?.image1 || 'NO IMAGE'}
          />
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              py: 1,
              gap: 3,
              ':last-child': { pb: 1 },
              flex: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box>
                      <MuiChip
                        label={`看護・介護サービス`}
                        sx={{
                          fontWeight: 700,
                          backgroundColor: '#94F2EA',
                          height: '24px',
                          fontSize: '14px',
                        }}
                      />
                    </Box>
                    <div className="highlight">
                      <Typography
                        variant="JP/Heading/s/Bold/Tight"
                        sx={{ verticalAlign: 'sub' }}
                      >
                        {listing.portal_office?.name}
                      </Typography>
                    </div>
                  </Box>
                </Box>
              </Box>

              {(isDxStation || isCarelogood) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  {isDxStation && <DxStationLabel />}
                  {isCarelogood && <CanRequestLabel />}
                </Box>
              )}
              <PartnershipsSummary
                portalOfficeId={Number(listing.portal_office_id)}
              />

              <Box
                sx={{
                  order: {
                    xs: 1,
                    md: 2,
                  },
                  '@container hospital-listing-container (width < 600px)': {
                    order: 1,
                  },
                }}
              >
                <Typography color="#595960" variant="JP/Body/m/Regular/Normal">
                  {postCode ? `${postCode} ${address}` : address}
                </Typography>

                {tel && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <PhoneIcon color="default" width={16} height={16} />
                    <Typography
                      color="#595960"
                      variant="JP/Body/m/Regular/Normal"
                    >
                      {tel}
                    </Typography>
                  </Box>
                )}

                {hasServiceCategoryItems && (
                  <Box sx={{ mt: 1 }}>
                    <ServiceList
                      services={serviceCategoryItems || []}
                      searchFilters={searchFilters}
                    />
                  </Box>
                )}
              </Box>

              {isCarelogood && (
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={3}
                  sx={{ order: 3 }}
                >
                  <Box
                    sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}
                  >
                    <EventIcon sx={{ color: '#C4C4C4', marginTop: '4px' }} />
                    <Typography
                      variant="JP/Body/m/Regular/Normal"
                      color="#595960"
                    >
                      営業時間
                    </Typography>
                  </Box>
                  <OnlyMemberComponent />
                </Stack>
              )}

              {listing.official_hp_url && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                    order: 4,
                  }}
                >
                  <HpUrlIcon
                    color="default"
                    width={18}
                    height={18}
                    sxProps={{
                      marginTop: '2px',
                    }}
                  />
                  <Typography
                    variant="JP/Body/m/Regular/Normal"
                    color="#595960"
                  >
                    {listing.official_hp_url}
                  </Typography>
                </Box>
              )}
            </Box>

            {isCarelogood && (
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  color: 'primary.main',
                  justifyContent: {
                    xs: 'center',
                    md: 'flex-start',
                  },
                  '@container hospital-listing-container (width < 600px)': {
                    justifyContent: 'center',
                  },
                }}
              >
                <MuiButton variant="text" onClick={onClickRequest}>
                  <Typography variant="JP/Body/s/Medium/None">
                    看護・介護サービスお問合せへ
                  </Typography>
                  <NavigateNextIcon fontSize="small" />
                </MuiButton>
              </Stack>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="JP/Body/m/Medium/Tight" color="#C4C4C4">
                掲載日：{publishedOnStr}
              </Typography>
              <Typography variant="JP/Body/m/Medium/Tight" color="#C4C4C4">
                最終更新日：{updatedOnStr}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </CardWrapper>
  );
}
